/**
 * Shared contract for the unified dashboard surface (Analytics / Shared / Org).
 *
 * The same section modules render on three surfaces; they differ on only two
 * axes, both captured by {@link DashboardSurface}. Tabs map to lazily-loaded
 * data {@link SliceId | slices}, not 1:1 to datasets. Backend computes all
 * figures and returns a {@link MetricStatus} per metric; the frontend only maps
 * that status to a theme colour.
 *
 * No "Analytics" naming here on purpose — these types are reused by the Shared
 * and Org dashboards too.
 */
/** Which data the surface is scoped to. Drives the data source and the
 * Targets-vs-Projects tab swap. */
export var DashboardScope;
(function (DashboardScope) {
    DashboardScope["Project"] = "project";
    DashboardScope["Organisation"] = "organisation";
})(DashboardScope || (DashboardScope = {}));
/** The domain sections. Values match the v18 section ids.
 *
 * `Compliance` is the odd one out: the other four are each ONE domain measured
 * on its own, while Compliance is a cross-cutting view of a single adopted
 * framework — it reads hours, spend, local-content and document commitments
 * together, filtered to the targets that framework generated. It is scoped by a
 * `frameworkId` route param rather than by the project alone. */
export var SectionId;
(function (SectionId) {
    SectionId["LabourHours"] = "labour";
    SectionId["SocialSpend"] = "spend";
    SectionId["LocalContent"] = "local";
    SectionId["Environmental"] = "env";
    SectionId["Compliance"] = "compliance";
    SectionId["GovAssureDocuments"] = "gov-documents";
    SectionId["GovAssureForms"] = "gov-forms";
    SectionId["GovAssureSurveys"] = "gov-surveys";
    SectionId["GovAssureRegisters"] = "gov-registers";
})(SectionId || (SectionId = {}));
/** A lazily-loaded data slice. One endpoint per slice; tabs reference a slice.
 * Breakdown serves several tabs from one load; Insights is deliberately its own
 * slice so it can later become an AI-generated narrative without touching
 * Targets. */
export var SliceId;
(function (SliceId) {
    SliceId["Summary"] = "summary";
    SliceId["Targets"] = "targets";
    SliceId["Insights"] = "insights";
    SliceId["Breakdown"] = "breakdown";
})(SliceId || (SliceId = {}));
/** Every tab id across all sections. The shared spine (overview/targets/insights)
 * plus the org-only Projects tab and the per-section specialised tabs. */
export var TabId;
(function (TabId) {
    // shared spine
    TabId["Overview"] = "overview";
    TabId["Targets"] = "targets";
    TabId["Insights"] = "insights";
    TabId["Projects"] = "projects";
    TabId["Credits"] = "credits";
    TabId["Submissions"] = "submissions";
    TabId["Responses"] = "responses";
    // labour hours
    TabId["Gender"] = "gender";
    TabId["EmploymentLevel"] = "employment-level";
    TabId["Cohorts"] = "cohorts";
    TabId["Packages"] = "packages";
    TabId["Occupations"] = "occupations";
    TabId["Geography"] = "geo";
    // social spend
    TabId["Suppliers"] = "suppliers";
    TabId["SpendMix"] = "mix";
    TabId["SupplierMap"] = "map";
    // local content
    TabId["AnzContent"] = "anz";
    TabId["MaterialOrigin"] = "origin";
    // environmental
    TabId["Emissions"] = "emissions";
    TabId["Energy"] = "energy";
    TabId["Water"] = "water";
    TabId["Waste"] = "waste";
    TabId["Materials"] = "materials";
    TabId["Transport"] = "transport";
})(TabId || (TabId = {}));
/** Threshold classification computed on the backend; the frontend maps it to a
 * theme colour (green / amber / red). */
export var MetricStatus;
(function (MetricStatus) {
    MetricStatus["Green"] = "green";
    MetricStatus["Amber"] = "amber";
    MetricStatus["Red"] = "red";
})(MetricStatus || (MetricStatus = {}));
/** Lower sentinel for an unbounded ("All time") range — earlier than any
 * reporting month the product can hold. */
export const ALL_TIME_START = "1900-01-01";
/** Upper sentinel for an unbounded ("All time") range. Matches the long-standing
 * `FUTURE_DATE` used by the organisation dashboard. */
export const ALL_TIME_END = "9000-01-01";
/** The unbounded range. Sentinels rather than `undefined` keep every request
 * shape identical whether or not the reader has chosen a period. */
export const ALL_TIME_RANGE = {
    endDate: ALL_TIME_END,
    startDate: ALL_TIME_START
};
/** Whether a range imposes no bound at all — a range at or beyond the sentinels
 * on both sides. Callers use this to skip filtering entirely rather than emit a
 * predicate that every row satisfies. */
export const isAllTimeRange = ({ endDate, startDate }) => startDate <= ALL_TIME_START && endDate >= ALL_TIME_END;
